import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "templates/Tables/DataTable";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { UserContext } from "App";
import { GetAllDevoteeDataAPI } from "APIs/DevoteeAPIs";
import { useNavigate } from "react-router-dom";
import { notify } from "App";
import MDInput from "components/MDInput";
import PersonIcon from "@mui/icons-material/Person";

import { DeleteDevoteeAPI, ActiveDevoteeAPI } from "APIs/DevoteeAPIs";
import { popUp, deleteMsg } from "components/Custom/AreYouSurePopUp";
// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
} from "layouts/receipt/components/SectionCard";

// Rows per page choices (first one is the default)
const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Same rule the view / edit pages use: a url ending in "/" means no picture was uploaded
const hasPicture = (picture) => !!picture && picture[picture.length - 1] !== "/";

// Ranu gold, same focus colour the cards give to their inputs (the search box sits outside a card)
const FOCUS_GOLD = "#C89B3C";

const searchFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: FOCUS_GOLD,
    borderWidth: "2px",
  },
  "& .MuiInputBase-root.Mui-focused": { boxShadow: "none", outline: "none" },
};

const viewButtonSx = ({ palette }) => ({
  color: palette.white.main,
  boxShadow: "none",
  transition: "background-color .3s ease, color .3s ease",
  "&:hover": {
    backgroundColor: palette.primary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// View, Edit, Activate and Delete all get the same fixed size
// (explicit width + height + border-box, so the outlined buttons' 1px border can't make them bigger)
const statusButtonSx = {
  // smaller on phones/tablets/laptops, full size only on wide screens (xxl >= 1400px)
  width: { xs: "4.75rem", xxl: "5.875rem" },
  minWidth: 0,
  height: { xs: "2.25rem", xxl: "2.5rem" },
  minHeight: { xs: "2.25rem", xxl: "2.5rem" },
  fontSize: { xs: "0.7rem", xxl: "0.75rem" },
  boxSizing: "border-box",
  padding: 0,
  lineHeight: 1,
};

const tableSx = ({ palette }) => ({
  "& thead th > div": { color: palette.primary.main },

  // Laptops / small desktops (< 1400px): tighter cells so the Action column always fits
  "@media (max-width: 1399.98px)": {
    "& th, & td": { paddingLeft: "10px !important", paddingRight: "10px !important" },
    "& td > div": { width: "auto" }, // long emails wrap instead of stretching the table
    "& td:last-of-type > div": { maxWidth: "16rem" },
  },

  // Desktop / large tablet (>= 900px): View, Edit and Delete always stay in ONE row
  "@media (min-width: 900px)": {
    "& td:last-of-type > div > div": { flexWrap: "nowrap" },
  },

  // Tablet and below (< 992px)
  "@media (max-width: 991.98px)": {
    "& th, & td": { paddingLeft: "8px !important", paddingRight: "8px !important" },
    "& td > div": { width: "auto" }, // lets the action buttons wrap
  },

  // Phones and small tablets (< 900px): every row becomes a card
  "@media (max-width: 899.98px)": {
    "& table, & tbody, & tr, & td": {
      display: "block",
      width: "100%",
      boxSizing: "border-box",
    },
    "& thead": { display: "none" },

    "& tbody tr": {
      boxSizing: "border-box",
      width: "auto",
      margin: "12px",
      padding: "8px 4px",
      border: `1px solid ${palette.light.main}`,
      borderRadius: "12px",
      background: palette.white.main,
    },

    "& td": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      textAlign: "right",
      borderBottom: "none",
      padding: "6px 12px !important",
    },
    "& td::before": {
      content: "attr(data-label)",
      textTransform: "uppercase",
      fontWeight: 700,
      fontSize: "0.7rem",
      color: palette.primary.main,
      textAlign: "left",
      flexShrink: 0,
    },
    "& td > div": { width: "auto", maxWidth: "65%", wordBreak: "break-word" },

    // Action cell: no label, the buttons sit side by side and share the full width
    "& td:last-of-type::before": { display: "none" },
    "& td:last-of-type > div": { width: "100%", maxWidth: "100%" },
    "& td:last-of-type > div > div": { flexWrap: "nowrap", gap: "8px" },
    "& td:last-of-type .MuiButton-root": {
      flex: "1 1 0",
      width: "auto",
      minWidth: 0,
      fontSize: "0.7rem",
      whiteSpace: "nowrap",
    },
  },

  "@media (max-width: 400px)": {
    "& tbody tr": { margin: "8px 6px" },
    "& td": { padding: "6px 8px !important", gap: "8px" },
    "& td > div": { maxWidth: "60%" },
    "& td:last-of-type > div": { maxWidth: "100%" },
    // still ONE row of buttons, just a little tighter so it fits 300px screens
    "& td:last-of-type > div > div": { gap: "6px" },
    "& td:last-of-type .MuiButton-root": { fontSize: "0.62rem", letterSpacing: 0 },
  },
});

const Devotee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [devotees, setDevotees] = useState([]);
  const userData = useContext(UserContext);
  const [rows, setRow] = useState([]);
  const [columns, setColumn] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [findText, setFindText] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalRecords, setTotalRecords] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Only the answer of the latest request may update the table (typing fast / changing page fast)
  const latestRequest = useRef(0);

  const loadDevotees = async () => {
    const requestId = ++latestRequest.current;
    setIsLoading(true);

    const response = await GetAllDevoteeDataAPI(
      userData.access_token,
      currentPage,
      pageSize,
      findText
    );

    console.log("DEVOTEE RESPONSE", response.data); // temporary

    if (requestId !== latestRequest.current) return;

    if (response?.is_success) {
      if (response.data.list.length === 0 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
        return;
      }
      setDevotees(response.data.list);
      setTotalEntries(response.data.total_number);
      setTotalRecords(response.data.total_number);
    } else {
      if (findText === "") notify(response?.data?.message || response?.message);
      setDevotees([]);
      setTotalEntries(0);
      setTotalRecords(0);
    }
    setIsLoading(false);
  };

  // Reload whenever the page, rows per page or the search text changes
  useEffect(() => {
    if (userData?.access_token === undefined) return undefined;

    // small delay while typing in the search box
    const timer = setTimeout(loadDevotees, findText !== "" ? 300 : 0);
    return () => clearTimeout(timer);
  }, [userData, currentPage, pageSize, findText]);

  const refreshList = () => loadDevotees();

  const handleDelete = async (id) => {
    const response = await DeleteDevoteeAPI(id, userData.access_token, dispatch);
    if (response !== null) refreshList();
  };

  // Activate an inactive devotee
  const handleActivate = async (id) => {
    const response = await ActiveDevoteeAPI(id, userData.access_token, dispatch);
    if (response !== null) refreshList();
  };

  useEffect(() => {
    setRow([
      ...devotees.map((item) => {
        // status 0 = deleted / inactive
        const isInactive = Number(item.status) === 0;

        return {
          // No picture (empty, or a bare folder url ending in "/") -> show a user icon, not a letter
          pic: (
            <MDAvatar
              bgColor="primary"
              src={hasPicture(item.picture) ? item.picture : undefined}
              size="md"
            >
              <PersonIcon
                style={{
                  fontSize: "36px",
                  width: "36px",
                  height: "36px",
                }}
              />
            </MDAvatar>
          ),
          name: `${item.first_name} ${item.last_name}`,
          phone: `${item.phone}`,
          email: `${item.email}`,
          status: (
            <MDBadge
              badgeContent={isInactive ? "INACTIVE" : "ACTIVE"}
              size="sm"
              color={isInactive ? "secondary" : "success"}
              variant="contained"
              container
            />
          ),
          action: (
            // flex + wrap: on small screens the buttons go to the next line instead of overflowing
            <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={1}>
              {isInactive ? (
                // Deleted devotee: only Activate. After activating, View / Edit / Delete come back.
                <MDButton
                  variant="outlined"
                  color="success"
                  sx={[ranuOutlinedButtonSx, statusButtonSx]}
                  onClick={async () => {
                    await popUp(handleActivate, item.devotee_id, {
                      title: "Activate devotee?",
                      text: "This devotee will be made active again.",
                      icon: "question",
                      confirmButtonText: "Yes, activate",
                    });
                  }}
                >
                  Activate
                </MDButton>
              ) : (
                <>
                  <MDButton
                    color="secondary"
                    sx={[viewButtonSx, statusButtonSx]}
                    onClick={() => {
                      navigate(`view/${item.devotee_id}`);
                    }}
                  >
                    view
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="primary"
                    sx={[ranuOutlinedButtonSx, statusButtonSx]}
                    onClick={() => {
                      navigate(`edit/${item.devotee_id}`);
                    }}
                  >
                    Edit
                  </MDButton>
                  <MDButton
                    color="error"
                    sx={statusButtonSx}
                    onClick={async () => {
                      await popUp(handleDelete, item.devotee_id, deleteMsg("devotee"));
                    }}
                  >
                    Delete
                  </MDButton>
                </>
              )}
            </MDBox>
          ),
        };
      }),
    ]);
    setColumn([
      { Header: "pic", accessor: "pic", width: "10%", align: "center" },
      { Header: "name", accessor: "name", width: "15%" },
      { Header: "phone", accessor: "phone", width: "15%" },
      { Header: "email", accessor: "email", width: "15%" },
      { Header: "status", accessor: "status", width: "10%", align: "center" },
      { Header: "action", accessor: "action", width: "25%", align: "center" },
    ]);
  }, [devotees, userData, findText]);

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      {/* Add button sits outside the table, top right (next to the page badge) */}
      <SectionHeader
        icon="group"
        label="DEVOTEE"
        endAction={
          <MDButton
            color="primary"
            sx={ranuButtonSx}
            onClick={() => {
              navigate("/devotee/add");
            }}
          >
            <MDTypography fontSize={15} fontWeight="bold" color="inherit" noWrap>
              Add Devotee
            </MDTypography>
          </MDButton>
        }
      />

      <MDBox mx={{ xs: -2, md: -3 }} mb={{ xs: -2, md: -3 }} sx={tableSx}>
        <DataTable
          isLoading={isLoading}
          table={{
            columns,
            rows,
          }}
          FindText={
            <MDInput
              placeholder="Search..."
              value={findText}
              size="small"
              fullWidth
              sx={searchFocusSx}
              onChange={(e) => {
                setFindText(e.target.value);
                setCurrentPage(1);
              }}
            />
          }
          serverPagination={{
            page: currentPage - 1,
            rowsPerPage: pageSize,
            totalPages: totalEntries,
            totalCount: totalRecords ?? undefined,
            currentCount: devotees.length,
            rowsPerPageOptions: PAGE_SIZE_OPTIONS,
            onPageChange: (newPage) => setCurrentPage(newPage + 1),
            onRowsPerPageChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />
      </MDBox>
    </MDBox>
  );
};

export default Devotee;
